import React, { useMemo, useRef, useState } from "react"
import { Button, Switch, notification } from "antd"
import { GoogleMaps, Icon } from "components"
import Strings from "utils/strings"
import { SectorsMapViewProps } from "./types"
import {
	DEPOT_COLOR,
	FALLBACK_COLOR,
	MAP_STYLE,
	SELECTION_COLOR,
	sectorColorForIndex,
} from "./utils"

const SectorsMapView: React.FC<SectorsMapViewProps> = ({
	orders,
	depots,
	availableSectors,
	hiddenSectors,
	onToggleSectorVisibility,
	onUpdateSectors,
}) => {
	const mapRef = useRef<google.maps.Map | null>(null)
	const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const [selected, setSelected] = useState<Set<number>>(new Set())
	// Visual-only filter for the sector list; empty sectors stay selectable in the "update sector" chips.
	const [hideEmptySectors, setHideEmptySectors] = useState(false)

	// Grows only, never shrinks, so a sector keeps its row after its last order is reassigned away.
	const knownSectorsRef = useRef<string[]>([])
	const sectors = useMemo(() => {
		// availableSectors covers contract lots with no orders yet (e.g. "1D").
		const current = Array.from(
			new Set([...orders.map((o) => String(o.sector)), ...availableSectors])
		).sort()
		current.forEach((sector) => {
			if (!knownSectorsRef.current.includes(sector)) {
				knownSectorsRef.current.push(sector)
			}
		})
		return [...knownSectorsRef.current]
	}, [orders, availableSectors])

	// Alphabetical, not discovery order — orders and availableSectors can load in
	// a different sequence across reloads (draft restore vs. API), which would
	// otherwise give the same sector a different color each time.
	const displaySectors = useMemo(() => [...sectors].sort(), [sectors])

	const sectorColorMap = useMemo(() => {
		const map: Record<string, string> = {}
		displaySectors.forEach((sector, i) => {
			map[sector] = sectorColorForIndex(i)
		})
		return map
	}, [displaySectors])

	const sectorColor = (sector: string) =>
		sectorColorMap[sector] || FALLBACK_COLOR

	const indicesForSector = (sector: string) =>
		orders.reduce<number[]>((acc, o, i) => {
			if (String(o.sector) === sector) acc.push(i)
			return acc
		}, [])

	const toggleSelected = (index: number) => {
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(index)) next.delete(index)
			else next.add(index)
			return next
		})
	}

	// Selects every order in the sector; clicking again while all are selected deselects them.
	const toggleSectorSelection = (sector: string) => {
		const sectorIndices = indicesForSector(sector)
		const allSelected = sectorIndices.every((i) => selected.has(i))

		setSelected((prev) => {
			const next = new Set(prev)
			sectorIndices.forEach((i) => (allSelected ? next.delete(i) : next.add(i)))
			return next
		})
	}

	const handleToggleVisibility = (sector: string, event: React.MouseEvent) => {
		event.stopPropagation()
		onToggleSectorVisibility(sector)

		// Hidden points shouldn't linger in the selection.
		setSelected((prev) => {
			const next = new Set(prev)
			indicesForSector(sector).forEach((i) => next.delete(i))
			return next
		})
	}

	const mapMarkers = useMemo(() => {
		const orderMarkers = orders
			.map((order, index) => {
				if (typeof order.lat !== "number" || typeof order.lon !== "number")
					return null
				if (hiddenSectors.has(String(order.sector))) return null

				const isSelected = selected.has(index)

				const isOnlyBike = Boolean(order.only_bike)
				const color = sectorColor(String(order.sector))

				return {
					lat: order.lat,
					lng: order.lon,
					onClick: () => toggleSelected(index),
					icon: {
						path: 0, // google.maps.SymbolPath.CIRCLE
						// Bike-only orders render hollow (white fill, colored ring).
						fillColor: isOnlyBike ? "white" : color,
						fillOpacity: 1,
						scale: isSelected ? 8 : isOnlyBike ? 7 : 6,
						strokeColor: isSelected
							? SELECTION_COLOR
							: isOnlyBike
								? color
								: "white",
						strokeWeight: isSelected ? 3 : isOnlyBike ? 3 : 1.5,
					},
					overlay: (
						<div style={{ minWidth: 180 }}>
							<div style={{ fontWeight: 600 }}>
								{order.name || order.company || "-"}
							</div>
							<div style={{ fontSize: 12, color: "#666" }}>
								{order.address || "-"}
							</div>
							<div style={{ fontSize: 12, marginTop: 4 }}>
								{Strings.sectorsMap.sector}: <strong>{order.sector}</strong>
							</div>
							<div style={{ fontSize: 12 }}>
								{Strings.sectorsMap.orderId}: <strong>{order.orderId}</strong>
							</div>
							{isOnlyBike && (
								<div style={{ fontSize: 12, marginTop: 4, color: "#b8860b" }}>
									{Strings.sectorsMap.onlyBike}
								</div>
							)}
						</div>
					),
				}
			})
			.filter((marker): marker is NonNullable<typeof marker> => !!marker)

		const depotMarkers = depots.map((depot) => ({
			lat: depot.lat,
			lng: depot.lng,
			// Diamond shape so depots read as distinct from delivery points.
			icon: {
				path: "M 0,-8 L 8,0 L 0,8 L -8,0 Z",
				fillColor: DEPOT_COLOR,
				fillOpacity: 1,
				scale: 1,
				strokeColor: "#fff",
				strokeWeight: 1.5,
			},
			overlay: <div style={{ fontWeight: 600 }}>{depot.name}</div>,
		}))

		return [...orderMarkers, ...depotMarkers]
	}, [orders, selected, depots, hiddenSectors, sectorColorMap])

	const boundingBox = useMemo(() => {
		const coords = orders
			.filter((o) => typeof o.lat === "number" && typeof o.lon === "number")
			.map((o) => ({ lat: o.lat, lng: o.lon }))
			.concat(depots)

		if (coords.length === 0) return undefined

		return {
			south: Math.min(...coords.map((c) => c.lat)),
			west: Math.min(...coords.map((c) => c.lng)),
			north: Math.max(...coords.map((c) => c.lat)),
			east: Math.max(...coords.map((c) => c.lng)),
		}
	}, [orders, depots])

	const legendItems = useMemo(() => {
		const counts: Record<string, number> = {}
		const selectedCounts: Record<string, number> = {}
		orders.forEach((order, index) => {
			const sector = String(order.sector)
			counts[sector] = (counts[sector] || 0) + 1
			if (selected.has(index)) {
				selectedCounts[sector] = (selectedCounts[sector] || 0) + 1
			}
		})
		return displaySectors.map((sector) => ({
			sector,
			count: counts[sector] || 0,
			selectedCount: selectedCounts[sector] || 0,
			color: sectorColor(sector),
		}))
	}, [displaySectors, orders, selected])

	const mapProxyRef = useMemo(
		() => ({
			get current() {
				return mapRef.current
			},
			set current(val: google.maps.Map | null) {
				mapRef.current = val
				if (val) setMapInstance(val)
			},
		}),
		[]
	)

	const applySectorToSelection = (sector: string) => {
		if (selected.size === 0) return
		const count = selected.size
		onUpdateSectors(Array.from(selected), sector)
		setSelected(new Set())
		notification.success({
			message: `${count} ${Strings.sectorsMap.pointsMovedToSector} ${sector}`,
			placement: "bottomRight",
			duration: 3,
		})
	}

	return (
		<div className="SectorsMapMap" style={{ display: "flex", gap: 16 }}>
			<div
				className="SectorsMapCanvas"
				style={{
					position: "relative",
					flex: 1,
					minWidth: 0,
					...(isFullscreen
						? {
								position: "fixed",
								top: 0,
								left: 0,
								width: "100vw",
								height: "100vh",
								zIndex: 1000,
								backgroundColor: "#fff",
							}
						: {}),
				}}
			>
				<GoogleMaps
					overlay
					overlaySize="small"
					overlayTrigger="hover"
					zoom={11}
					boundingBox={boundingBox}
					mapRef={mapProxyRef}
					mapContainerStyle={{
						width: "100%",
						height: isFullscreen ? "100vh" : "100%",
						borderRadius: isFullscreen ? 0 : 8,
					}}
					options={{
						mapTypeControl: false,
						fullscreenControl: false,
						styles: MAP_STYLE,
					}}
					markers={mapMarkers}
				/>

				<div style={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}>
					<Button
						size="small"
						onClick={() => setIsFullscreen(!isFullscreen)}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 4,
							boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
						}}
					>
						<Icon name={isFullscreen ? "close" : "frame"} />
						{isFullscreen
							? Strings.generic.close
							: Strings.sectorsMap.fullscreen}
					</Button>
				</div>
			</div>

			{!isFullscreen && (
				<div className="SectorsMapSidebar">
					<div className="SectorsMapSelection">
						<span className="SectorsMapSidebarLabel">
							{Strings.sectorsMap.selection}
						</span>

						<div
							className={`SectorsMapStep${selected.size === 0 ? " SectorsMapStepActive" : " SectorsMapStepDone"}`}
						>
							<span className="SectorsMapStepNumber">
								{selected.size === 0 ? "1" : <Icon name="tick" />}
							</span>
							<p className="SectorsMapHint">
								{selected.size > 0
									? `${selected.size} ${Strings.sectorsMap.pointsSelected}`
									: Strings.sectorsMap.clickToSelect}
							</p>
						</div>

						<div
							className={`SectorsMapStep${selected.size > 0 ? " SectorsMapStepActive" : ""}`}
						>
							<span className="SectorsMapStepNumber">2</span>
							<p className="SectorsMapHint">
								{Strings.sectorsMap.updateSector}
							</p>
						</div>

						{selected.size > 0 && (
							<>
								<div className="SectorsMapSectorChips">
									{displaySectors.map((s) => (
										<button
											key={s}
											type="button"
											className="SectorsMapSectorChip"
											onClick={() => applySectorToSelection(s)}
										>
											<span
												className="SectorsMapSectorChipDot"
												style={{ background: sectorColor(s) }}
											/>
											{s}
										</button>
									))}
								</div>
								<Button
									block
									onClick={() => setSelected(new Set())}
									style={{ marginTop: 8 }}
								>
									{Strings.sectorsMap.clearSelection}
								</Button>
							</>
						)}
					</div>

					<div className="SectorsMapLegend">
						<div className="SectorsMapLegendHeader">
							<span className="SectorsMapSidebarLabel">
								{Strings.sectorsMap.sector}
							</span>
							<span className="SectorsMapLegendTotal">
								{orders.length} {Strings.sectorsMap.ordersLoaded.toLowerCase()}
							</span>
						</div>
						<p className="SectorsMapHint">
							{Strings.sectorsMap.clickSectorToSelect}
						</p>
						<label className="SectorsMapLegendFilter">
							<Switch
								size="small"
								checked={hideEmptySectors}
								onChange={setHideEmptySectors}
							/>
							{Strings.sectorsMap.hideEmptySectors}
						</label>
						<div className="SectorsMapLegendRows">
							{legendItems
								.filter((item) => !hideEmptySectors || item.count > 0)
								.map((item) => {
									const isHidden = hiddenSectors.has(item.sector)

									return (
										<div
											key={item.sector}
											className={`SectorsMapLegendItem${isHidden ? " SectorsMapLegendItemHidden" : ""}`}
											onClick={() => toggleSectorSelection(item.sector)}
											title={Strings.sectorsMap.clickSectorToSelect}
										>
											<span
												className="SectorsMapLegendDot"
												style={{ backgroundColor: item.color }}
											/>
											<span className="SectorsMapLegendSectorName">
												{item.sector}
											</span>
											<strong className="SectorsMapLegendCount">
												{item.selectedCount > 0 && (
													<span className="SectorsMapLegendSelectedCount">
														{item.selectedCount}/
													</span>
												)}
												{item.count}
											</strong>
											<Icon
												name={isHidden ? "eye-outline" : "eye"}
												className="SectorsMapLegendVisibilityToggle"
												title={
													isHidden
														? Strings.sectorsMap.showSector
														: Strings.sectorsMap.hideSector
												}
												onClick={(e) => handleToggleVisibility(item.sector, e)}
											/>
										</div>
									)
								})}
						</div>

						<div className="SectorsMapLegendKey">
							<span className="SectorsMapLegendDot SectorsMapLegendDotHollow" />
							<span>{Strings.sectorsMap.onlyBike}</span>
						</div>
						<div className="SectorsMapLegendKey">
							<span className="SectorsMapLegendDot SectorsMapLegendDotDepot" />
							<span>{Strings.sectorsMap.depot}</span>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default SectorsMapView
